import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Field } from 'react-final-form';
import { FORM_ERROR } from 'final-form';
import moment from 'moment';
import { Box, Button, Grid } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useAsyncFn } from 'react-use';
import CustomSelect from 'components/Forms/CustomSelect';
import CustomDatePicker from 'components/Forms/CustomDatePicker';
import CustomTextField from 'components/Forms/TextField';
import SubmitErrorMessage from 'components/Forms/SubmitErrorMessage';
import { getTrainingModulesList } from 'http/trainingModules';
import { getTrainingPartsList } from 'http/trainingParts';
import { resolveApiErrorMessage } from 'utils';
import { formatTrainingModuleLabel } from './helpers/formatTrainingLabels';

const toSelectOption = (value, label) => ({ value, label });

const ConfirmExternalEvidenceForm = ({ onSubmitForm, rejectAction, submitting, initialValues }) => {
  const { t } = useTranslation();
  const [moduleOptions, setModuleOptions] = useState([]);
  const [partOptions, setPartOptions] = useState([]);

  const [, loadModules] = useAsyncFn(async () => {
    const response = await getTrainingModulesList({ page: 0, pageSize: 500, status: 'active' });
    if (response?.error || (response?.statusCode && response.statusCode >= 400)) {
      return response;
    }
    const items = response?.items || [];
    setModuleOptions(
      items.map(item => toSelectOption(item.id, formatTrainingModuleLabel(item.code, item.name))),
    );
    return response;
  }, []);

  const [, loadParts] = useAsyncFn(async trainingModuleId => {
    if (!trainingModuleId) {
      setPartOptions([]);
      return null;
    }
    const response = await getTrainingPartsList({
      trainingModuleId,
      page: 0,
      pageSize: 200,
    });
    if (response?.error || (response?.statusCode && response.statusCode >= 400)) {
      setPartOptions([]);
      return response;
    }
    const items = (response?.items || []).filter(part => ['scheduled', 'active', 'retired'].includes(part.status));
    setPartOptions(
      items.map(part =>
        toSelectOption(part.id, `${part.code} ${part.version}.${part.revision} (${part.trainingMethod})`),
      ),
    );
    return items;
  }, []);

  useEffect(() => {
    loadModules();
  }, [loadModules]);

  useEffect(() => {
    if (initialValues?.trainingModuleId) {
      loadParts(initialValues.trainingModuleId);
    }
  }, [initialValues?.trainingModuleId, loadParts]);

  const formInitialValues = initialValues
    ? {
        trainingModule: initialValues.trainingModuleId
          ? toSelectOption(
              initialValues.trainingModuleId,
              initialValues.trainingModuleLabel || String(initialValues.trainingModuleId),
            )
          : null,
        trainingPart: initialValues.trainingPartId
          ? toSelectOption(
              initialValues.trainingPartId,
              initialValues.trainingPartLabel || String(initialValues.trainingPartId),
            )
          : null,
        issuedAt: initialValues.issuedAt ? moment(initialValues.issuedAt).toDate() : null,
        expiresAt: initialValues.expiresAt ? moment(initialValues.expiresAt).toDate() : null,
        notes: initialValues.notes || '',
      }
    : undefined;

  const validate = values => {
    const errors = {};
    if (!values.trainingModule?.value) {
      errors.trainingModule = t('errors.fieldIsRequired');
    }
    if (!values.trainingPart?.value) {
      errors.trainingPart = t('errors.fieldIsRequired');
    }
    if (!values.issuedAt) {
      errors.issuedAt = t('errors.fieldIsRequired');
    }
    return errors;
  };

  const handleSubmit = async values => {
    const payload = {
      trainingModuleId: values.trainingModule.value,
      trainingPartId: values.trainingPart.value,
      issuedAt: values.issuedAt ? moment(values.issuedAt).format('YYYY-MM-DD') : null,
      expiresAt: values.expiresAt ? moment(values.expiresAt).format('YYYY-MM-DD') : undefined,
      notes: values.notes || undefined,
    };
    const response = await onSubmitForm(payload);
    if (response?.error || (response?.statusCode && response.statusCode >= 400)) {
      return {
        [FORM_ERROR]: resolveApiErrorMessage(response.message, {
          t,
          fallbackMessage: t('alerts.serverNotAvailable'),
        }),
      };
    }
    return undefined;
  };

  return (
    <Form
      onSubmit={handleSubmit}
      validate={validate}
      initialValues={formInitialValues}
      render={({ handleSubmit: submit, submitting: formSubmitting, submitError, values, form }) => (
        <form onSubmit={submit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Field name="trainingModule">
                {({ input, meta }) => (
                  <CustomSelect
                    input={input}
                    meta={meta}
                    id="trainingModule"
                    label={t('tablesLabels.trainingModule')}
                    options={moduleOptions}
                    fullWidth
                    onChange={option => {
                      input.onChange(option);
                      form.change('trainingPart', null);
                      loadParts(option?.value);
                    }}
                  />
                )}
              </Field>
            </Grid>
            <Grid item xs={12}>
              <Field name="trainingPart">
                {({ input, meta }) => (
                  <CustomSelect
                    input={input}
                    meta={meta}
                    id="trainingPart"
                    label={t('tablesLabels.trainingPart')}
                    options={partOptions}
                    isDisabled={!values.trainingModule?.value}
                    fullWidth
                  />
                )}
              </Field>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Field
                name="issuedAt"
                component={CustomDatePicker}
                label={t('tablesLabels.performedDate')}
                disableFuture
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Field name="expiresAt" component={CustomDatePicker} label={t('tablesLabels.expiryDate')} />
            </Grid>
            <Grid item xs={12}>
              <Field
                name="notes"
                component={CustomTextField}
                label={t('formLabels.notes', 'Notes')}
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
          {submitError && <SubmitErrorMessage>{submitError}</SubmitErrorMessage>}
          <Box mt={3} display="flex" justifyContent="flex-end" style={{ gap: 12 }}>
            <Button onClick={rejectAction} disabled={submitting || formSubmitting}>
              {t('buttons.cancel')}
            </Button>
            <Button type="submit" color="primary" variant="contained" disabled={submitting || formSubmitting}>
              {t('buttons.yes')}
            </Button>
          </Box>
        </form>
      )}
    />
  );
};

ConfirmExternalEvidenceForm.propTypes = {
  onSubmitForm: PropTypes.func.isRequired,
  rejectAction: PropTypes.func.isRequired,
  submitting: PropTypes.bool,
  initialValues: PropTypes.shape({
    trainingModuleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    trainingModuleLabel: PropTypes.string,
    trainingPartId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    trainingPartLabel: PropTypes.string,
    issuedAt: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    expiresAt: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    notes: PropTypes.string,
  }),
};

ConfirmExternalEvidenceForm.defaultProps = {
  submitting: false,
  initialValues: null,
};

export default ConfirmExternalEvidenceForm;
